<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Webhooks</h5>
        <p class="page-sub">Receive real-time HTTP notifications when events occur across all modules</p>
      </div>
      <button class="btn-primary-action" @click="openNew">
        <font-awesome-icon icon="plus" /> New Webhook
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 3" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!webhooks.length" class="empty-state">
      <font-awesome-icon icon="bolt" class="empty-icon" />
      <p>No webhooks configured yet.</p>
      <p class="empty-sub">Subscribe to events and receive live HTTP POST notifications to your endpoint.</p>
    </div>

    <!-- Webhook cards -->
    <div v-else class="wh-list">
      <div v-for="w in webhooks" :key="w.id" class="wh-card" :class="{ 'wh-card--inactive': !w.is_active }">
        <div class="wh-top">
          <div class="wh-left">
            <div class="wh-name">{{ w.name }}</div>
            <div class="wh-url"><font-awesome-icon icon="link" class="url-icon" />{{ w.url }}</div>
            <div class="wh-events">
              <span v-if="w.events.includes('*')" class="ev-chip ev-chip--all">All events</span>
              <template v-else>
                <span v-for="e in w.events.slice(0, 5)" :key="e" class="ev-chip">{{ e }}</span>
                <span v-if="w.events.length > 5" class="ev-chip ev-chip--more">+{{ w.events.length - 5 }} more</span>
              </template>
            </div>
          </div>
          <div class="wh-actions">
            <span v-if="w.is_active" class="status-dot status-dot--green" title="Active"></span>
            <span v-else class="status-dot status-dot--red" title="Paused"></span>
            <button class="btn-ghost btn-sm" @click="openEdit(w)"><font-awesome-icon icon="edit" /> Edit</button>
            <button class="btn-ghost btn-sm" @click="sendTest(w)" :disabled="testingId === w.id">
              <font-awesome-icon v-if="testingId === w.id" icon="spinner" spin />
              <font-awesome-icon v-else icon="paper-plane" />
              Test
            </button>
            <button class="btn-ghost btn-sm" @click="openDeliveries(w)"><font-awesome-icon icon="list" /> Log</button>
            <button class="btn-ghost btn-sm" @click="rotateSecret(w)" title="Rotate signing secret"><font-awesome-icon icon="sync-alt" /></button>
            <button class="btn-ghost btn-ghost--red btn-sm" @click="deleteWebhook(w)"><font-awesome-icon icon="trash" /></button>
          </div>
        </div>
        <div class="wh-footer">
          <span class="wh-stat">Last triggered: {{ w.last_triggered_at ? fmtDatetime(w.last_triggered_at) : 'Never' }}</span>
          <span v-if="w.failure_count > 0" class="wh-stat wh-stat--warn">
            <font-awesome-icon icon="exclamation-triangle" /> {{ w.failure_count }} failure{{ w.failure_count !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit modal ───────────────────────────────────────────── -->
    <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <h5 class="modal-title">{{ editWebhook ? 'Edit Webhook' : 'New Webhook' }}</h5>
          <button class="modal-close" @click="showForm = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input v-model="form.name" class="form-input" placeholder="e.g. HR system sync" />
          </div>
          <div class="form-row">
            <label class="form-label">Endpoint URL <span class="req">*</span></label>
            <input v-model="form.url" type="url" class="form-input" placeholder="https://your-system.com/webhooks/inventflux" />
          </div>
          <div class="form-row">
            <label class="form-label">Subscribe to Events <span class="req">*</span></label>
            <div class="ev-select-bar">
              <button type="button" class="btn-ghost btn-sm" @click="selectAll">All events (*)</button>
              <button type="button" class="btn-ghost btn-sm" @click="form.events = []">Clear</button>
            </div>
            <div v-for="(evs, group) in eventGroups" :key="group" class="ev-group">
              <div class="ev-group-title">{{ group }}</div>
              <div class="ev-grid">
                <label v-for="e in evs" :key="e" class="ev-check">
                  <input type="checkbox" :value="e" v-model="form.events" :disabled="form.events.includes('*')" />
                  <span>{{ e }}</span>
                </label>
              </div>
            </div>
          </div>
          <div v-if="editWebhook" class="form-row">
            <label class="form-label">Active</label>
            <label class="toggle-switch">
              <input type="checkbox" v-model="form.is_active" />
              <span class="toggle-track"></span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="showForm = false">Cancel</button>
          <button class="btn-primary-action" @click="saveWebhook" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (editWebhook ? 'Save Changes' : 'Create Webhook') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── New webhook secret modal ─────────────────────────────────────── -->
    <div v-if="newSecret" class="modal-overlay">
      <div class="modal-box" style="max-width:480px;">
        <div class="modal-head">
          <h5 class="modal-title">{{ newSecretTitle }}</h5>
        </div>
        <div class="modal-body">
          <p class="reveal-warning">Copy this signing secret now — it will not be shown again in full.</p>
          <div class="key-reveal-box">
            <code>{{ newSecret }}</code>
            <button class="copy-btn" @click="copyToClipboard(newSecret)">
              <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
          <div class="key-usage-box">
            <div class="key-usage-title">Verify deliveries in your endpoint</div>
            <pre class="key-usage-code">// Node.js example
const crypto = require('crypto')
const sig = req.headers['x-inventflux-sign']
const expected = 'sha256=' + crypto
  .createHmac('sha256', SECRET)
  .update(req.rawBody)
  .digest('hex')
if (sig !== expected) return res.status(401).end()</pre>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-action" @click="newSecret = null; copied = false">Done</button>
        </div>
      </div>
    </div>

    <!-- ── Delivery log modal ────────────────────────────────────────────── -->
    <div v-if="deliveriesTarget" class="modal-overlay" @click.self="deliveriesTarget = null">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <h5 class="modal-title">Delivery Log — {{ deliveriesTarget.name }}</h5>
          <button class="modal-close" @click="deliveriesTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="max-height:65vh;overflow-y:auto;">
          <div v-if="deliveriesLoading" style="text-align:center;padding:30px;color:#aab8cc;">
            <font-awesome-icon icon="spinner" spin /> Loading…
          </div>
          <div v-else-if="!deliveries.length" style="text-align:center;padding:30px;font-size:13px;color:#aab8cc;">
            No deliveries yet.
          </div>
          <div v-else class="dl-list">
            <div v-for="d in deliveries" :key="d.id" class="dl-item" :class="{ 'dl-item--fail': !d.success }">
              <div class="dl-top">
                <span class="ev-chip" style="font-size:11px;">{{ d.event }}</span>
                <span class="status-chip" :class="d.success ? 'status-chip--green' : 'status-chip--red'">
                  {{ d.response_status ?? 'Error' }}
                </span>
                <span v-if="d.attempt > 1" class="status-chip status-chip--amber">Attempt {{ d.attempt }}</span>
                <span class="dl-time">{{ fmtDatetime(d.delivered_at || d.created_at) }}</span>
                <span v-if="d.duration_ms != null" class="dl-ms">{{ d.duration_ms }}ms</span>
              </div>
              <div v-if="d.response_body" class="dl-body">{{ truncate(d.response_body, 200) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'DeveloperWebhooks',
  setup() { return { toast: useToast() } },
  data() {
    return {
      webhooks: [], loading: true,
      showForm: false, editWebhook: null,
      form: { name: '', url: '', events: [], is_active: true },
      saving: false, testingId: null,
      eventGroups: {}, allEvents: [],
      newSecret: null, newSecretTitle: '', copied: false,
      deliveriesTarget: null, deliveries: [], deliveriesLoading: false,
    }
  },
  async created() {
    await this.loadEvents()
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const r = await axios.get('developer/webhooks')
        this.webhooks = r.data.data ?? []
      } catch { this.toast.error('Failed to load webhooks.') }
      finally { this.loading = false }
    },
    async loadEvents() {
      try {
        const r = await axios.get('developer/webhooks/meta/events')
        this.allEvents   = r.data.data?.events ?? []
        this.eventGroups = r.data.data?.grouped ?? {}
      } catch { /* non-critical */ }
    },
    openNew() {
      this.editWebhook = null
      this.form = { name: '', url: '', events: [], is_active: true }
      this.showForm = true
    },
    openEdit(w) {
      this.editWebhook = w
      this.form = { name: w.name, url: w.url, events: [...(w.events || [])], is_active: w.is_active }
      this.showForm = true
    },
    selectAll() { this.form.events = ['*'] },
    async saveWebhook() {
      if (!this.form.name.trim()) { this.toast.error('Name is required.'); return }
      if (!this.form.url.trim())  { this.toast.error('Endpoint URL is required.'); return }
      if (!this.form.events.length) { this.toast.error('Select at least one event.'); return }
      this.saving = true
      try {
        if (this.editWebhook) {
          await axios.put(`developer/webhooks/${this.editWebhook.id}`, this.form)
          this.toast.success('Webhook updated.')
          this.showForm = false
          this.load()
        } else {
          const r = await axios.post('developer/webhooks', this.form)
          this.newSecret      = r.data.data?.secret
          this.newSecretTitle = 'Webhook Created — Signing Secret'
          this.showForm = false
          this.load()
        }
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    async sendTest(w) {
      this.testingId = w.id
      try {
        await axios.post(`developer/webhooks/${w.id}/test`)
        this.toast.success('Test ping queued — check the delivery log in a moment.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to send test.') }
      finally { this.testingId = null }
    },
    async rotateSecret(w) {
      if (!confirm(`Rotate signing secret for "${w.name}"? Your endpoint will need to be updated immediately.`)) return
      try {
        const r = await axios.post(`developer/webhooks/${w.id}/rotate-secret`)
        this.newSecret      = r.data.data?.secret
        this.newSecretTitle = 'Signing Secret Rotated'
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to rotate secret.') }
    },
    async deleteWebhook(w) {
      if (!confirm(`Delete webhook "${w.name}"? Deliveries will stop immediately.`)) return
      try {
        await axios.delete(`developer/webhooks/${w.id}`)
        this.toast.success('Webhook deleted.')
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete.') }
    },
    async openDeliveries(w) {
      this.deliveriesTarget = w; this.deliveries = []; this.deliveriesLoading = true
      try {
        const r = await axios.get(`developer/webhooks/${w.id}/deliveries`)
        this.deliveries = r.data.data ?? []
      } catch { this.toast.error('Failed to load deliveries.') }
      finally { this.deliveriesLoading = false }
    },
    copyToClipboard(text) {
      navigator.clipboard?.writeText(text)
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    truncate(s, n) { return s && s.length > n ? s.slice(0, n) + '…' : s },
    fmtDatetime(d) {
      return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.wh-list { display:flex; flex-direction:column; gap:12px; }
.wh-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; }
.wh-card--inactive { opacity:.65; background:#fafafa; }
.wh-top  { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.wh-left { flex:1; min-width:0; }
.wh-name { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:6px; }
.wh-url  { font-size:12px; color:#3d5068; font-family:monospace; margin-bottom:8px; display:flex; align-items:center; gap:5px; word-break:break-all; }
.url-icon { color:#aab8cc; font-size:11px; flex-shrink:0; }
.wh-events { display:flex; gap:5px; flex-wrap:wrap; }
.wh-actions { display:flex; gap:6px; align-items:center; flex-wrap:wrap; flex-shrink:0; }
.wh-footer  { display:flex; gap:16px; flex-wrap:wrap; margin-top:10px; padding-top:10px; border-top:1px solid #f0f4f8; }
.wh-stat    { font-size:11px; color:#aab8cc; }
.wh-stat--warn { color:#c2410c; }

.status-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
.status-dot--green { background:#16a34a; }
.status-dot--red   { background:#dc2626; }

.ev-chip    { display:inline-flex; align-items:center; padding:2px 7px; border-radius:20px; font-size:11px; font-weight:500; background:#f0f4f8; color:#3d5068; }
.ev-chip--all  { background:#eff6ff; color:#1d4ed8; font-weight:700; }
.ev-chip--more { background:#f0f4f8; color:#7a90b0; }

.ev-select-bar { display:flex; gap:8px; margin-bottom:10px; }
.ev-group { margin-bottom:14px; }
.ev-group-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:8px; }
.ev-grid  { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.ev-check { display:flex; align-items:center; gap:7px; padding:6px 10px; border:1px solid #e4eaf2; border-radius:7px; cursor:pointer; font-size:12px; color:#1a2b45; }
.ev-check input { cursor:pointer; }
.ev-check:hover { background:#f8fafc; }

.dl-list { display:flex; flex-direction:column; gap:8px; }
.dl-item { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:12px 14px; }
.dl-item--fail { background:#fff5f5; border-color:#fecaca; }
.dl-top  { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.dl-time { font-size:11px; color:#aab8cc; margin-left:auto; }
.dl-ms   { font-size:11px; color:#7a90b0; }
.dl-body { margin-top:8px; font-family:monospace; font-size:11px; color:#4a5e78; background:#fff; border-radius:6px; padding:8px 10px; border:1px solid #e4eaf2; word-break:break-all; }

.status-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
.status-chip--green { background:#dcfce7; color:#16a34a; }
.status-chip--red   { background:#fee2e2; color:#dc2626; }
.status-chip--amber { background:#fef3c7; color:#d97706; }

.reveal-warning { font-size:13px; color:#92400e; background:#fef3c7; border-radius:8px; padding:10px 14px; margin:0 0 12px; }
.key-reveal-box { display:flex; align-items:center; gap:10px; background:#f0f4f8; border-radius:10px; padding:12px 14px; margin-bottom:12px; overflow:hidden; }
.key-reveal-box code { flex:1; font-size:12px; word-break:break-all; color:#1a2b45; }
.copy-btn { flex-shrink:0; background:#004AAD; color:#fff; border:none; cursor:pointer; padding:6px 12px; border-radius:6px; font-size:12px; font-weight:600; font-family:inherit; }
.key-usage-box { background:#1a2b45; border-radius:10px; padding:14px 16px; }
.key-usage-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin-bottom:8px; }
.key-usage-code  { margin:0; font-size:11px; color:#e2e8f0; white-space:pre-wrap; word-break:break-all; font-family:monospace; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:80px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-state { text-align:center; padding:70px 20px; color:#aab8cc; }
.empty-icon  { font-size:48px; margin-bottom:14px; opacity:.5; }
.empty-state p { margin:0 0 6px; font-size:14px; }
.empty-sub   { font-size:13px !important; color:#aab8cc !important; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:5px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:12px; font-weight:600; padding:0 12px; height:32px; border-radius:7px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.5; cursor:not-allowed; }
.btn-ghost--red   { color:#dc2626; }
.btn-ghost--red:hover   { background:#fff5f5; border-color:#fecaca; }
.btn-sm { height:30px; padding:0 10px; font-size:11px; }

.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:560px; box-shadow:0 24px 60px rgba(0,0,0,.2); max-height:90vh; display:flex; flex-direction:column; }
.modal-box--wide { max-width:720px; }
.modal-head   { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; flex-shrink:0; }
.modal-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px; }
.modal-body   { padding:20px 22px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 22px; border-top:1px solid #f0f4f8; flex-shrink:0; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }
@media(max-width:520px) { .ev-grid { grid-template-columns:1fr; } }
</style>
